/**
 * @fileoverview System-web adapter for the shared fire-hazard overlay service.
 * @module system-web/services/fireHazardOverlayService
 *
 * Responsibilities:
 * - Inject the system API endpoint, authenticated HTTP client, and telemetry client.
 * - Preserve the established service exports consumed by system-web hooks and tests.
 * Dependencies:
 * - apiFetch, system-web telemetry, and @firesquirrel/fire-hazard-map-layers.
 * Architecture:
 * - Minimal host wiring around package-owned query, clamping, telemetry, and error behavior.
 * Notes:
 * - Read-only remote reference data; failures leave existing map source data untouched.
 *
 * @localFirst
 * Source of Truth: system API fire-hazard reference-data endpoint.
 * Write Flow: None.
 * Sync Strategy: On-demand viewport reads.
 * Conflict Resolution: Not applicable.
 */

import { apiFetch } from '../apiClient';
import { telemetry } from './telemetry';
import {
  clampFireHazardOverlayBounds,
  createFireHazardOverlayService,
  createFireHazardTelemetryAdapter,
  createUrlQueryHttpAdapter,
} from '@firesquirrel/fire-hazard-map-layers';

export type {
  FireHazardOverlayBounds,
  FireHazardOverlayFeatureCollection,
} from '@firesquirrel/fire-hazard-map-layers';

const service = createFireHazardOverlayService({
  endpoint: '/api/system/fire-hazard/overlay',
  failureContext: 'fire_hazard_overlay_failed',
  http: createUrlQueryHttpAdapter(async <T>(url: string) => apiFetch(url) as Promise<T>),
  telemetry: createFireHazardTelemetryAdapter(telemetry),
});

/**
 * @process clampBoundsToServerLimit
 * Steps: Delegate raw viewport bounds to the package-owned canonical clamp.
 * Inputs: Raw WGS84 viewport bounds.
 * Outputs: Server-safe viewport bounds.
 * Failure Handling: Pure operation with no side effects.
 * Idempotency: REQUIRED.
 */
export const clampBoundsToServerLimit = clampFireHazardOverlayBounds;

/**
 * @process fetchFireHazardOverlay
 * Steps: Delegate a viewport read to the configured shared service.
 * Inputs: Raw WGS84 viewport bounds.
 * Outputs: Hazard-zone GeoJSON FeatureCollection.
 * Failure Handling: Shared service tracks and rethrows failures.
 * Idempotency: REQUIRED for equivalent API state.
 */
export const fetchFireHazardOverlay = service.fetchFireHazardOverlay;
